import React, { useEffect, useState } from 'react';
import { Formik, FormikErrors } from 'formik';
import { v4 as uuid } from 'uuid';
import { Application, Catalogue, Modal, useWizardEffect } from '../../../common';
import { Delivery, Recipient, Solution } from '../../../common/types/application/opportunity';
import {
  SimpleWizard,
  SimpleWizardPageStep,
} from '../../../common/components/simple-wizard/simple-wizard';
import { ModalStepOne } from './modal-step-one/modal-step-one';
import { ModalStepThree } from './modal-step-three/modal-step-three';
import { ModalStepTwo } from './modal-step-two/modal-step-two';
import {
  hasNoSpecialCharacters,
  isValidID,
  isValidMobileNumber,
  isValidName,
} from '../../../../helpers/validation';
import { stripWhitespace } from '../../../../helpers/formatting';
import { SOUTH_AFRICA } from '../../../common/helpers/countries';
import { isUserUnderAgeIdNumber, validateAddressField } from 'src/features/common/helpers';
import { updateDeliverySolution } from 'src/features/common/helpers/update-delivery-solution';
import { DeliveryDetails } from '../../types/delivery-details';
import { notify, Toast } from 'src/features/common/components/toast/toast';
import { findAddress } from 'src/features/order/helpers/delivery-helpers';
import { useApplication } from 'src/hooks/useApplication';

export type DeliveryAddressModalProps = {
  catalogue: Catalogue;
  application: Application;
  deliveryDetails?: DeliveryDetails;
  show: boolean;
  setShow: (show: boolean) => void;
  valuesToEdit?: Delivery;
  onSave?: (delivery: Delivery, deliveries?: Delivery[]) => void;
  automationIdSuffix: string;
  jumpToStep?: number;
  saveAddress?: (delivery: Delivery) => void;
  isBrokerEditAddress: boolean;
};

type ModalStepThreeErrors = {
  solutions: string[];
  newDelivery: string[];
};

export function DeliveryAddressModal(props: DeliveryAddressModalProps) {
  const { orderTealiumService } = useApplication();
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const TOTAL_STEPS = 3;

  const { setOnNextEventHandler, setBackButtonEnabled, setNextButtonEnabled } =
    useWizardEffect(() => {
      return {
        nextButtonLabel:
          currentStepIndex === 2 ? getFinalStepNextButtonLabel(!!props.valuesToEdit) : 'Next',
        backButtonVisible: currentStepIndex > 0,
        backButtonEnabled: true,
      };
    }, [currentStepIndex]);

  useEffect(() => {
    if (props.show) {
      setCurrentStepIndex(props.jumpToStep || 0);
    }
  }, [props.show]);

  const getSteps = (values: Delivery) => {
    const steps: SimpleWizardPageStep[] = [
      {
        page: (pageProps) => (
          <ModalStepOne
            isEditing={!!props.valuesToEdit}
            wizardProps={pageProps}
            brokerLogin={!!props.application.brokerLogin}
            automationIdSuffix={props.automationIdSuffix}
          />
        ),
      },
      {
        page: ({ navigateToNextStep, navigateToPreviousStep }) => (
          <ModalStepTwo
            navigateToNextStep={navigateToNextStep}
            navigateToPreviousStep={navigateToPreviousStep}
            automationIdSuffix={props.automationIdSuffix}
            brokerLogin={!!props.application.brokerLogin}
          />
        ),
      },
      {
        page: (pageProps) => (
          <ModalStepThree
            application={props.application}
            catalogue={props.catalogue}
            wizardProps={pageProps}
            delivery={values}
            deliveryExisting={props.valuesToEdit}
            automationIdSuffix={props.automationIdSuffix}
          />
        ),
      },
    ];
    return steps;
  };

  const navigateToNextStep = () => {
    orderTealiumService?.onAddressDeliveryAction();
    if (currentStepIndex < TOTAL_STEPS - 1) {
      setCurrentStepIndex((currentValue) => currentValue + 1);
    }
  };

  const navigateToPreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((currentValue) => currentValue - 1);
    }
  };

  const getInitialValues = (): Delivery => {
    if (props.valuesToEdit) {
      return props.valuesToEdit;
    }

    return { address: {}, receiverDetails: {}, solutions: [{}], newDelivery: [{}] } as Delivery;
  };

  const validateRecipient = (values: Recipient) => {
    const errors: FormikErrors<Recipient> = {};

    if (!isValidName(values.managerName) || !values?.managerName) {
      errors.managerName = 'The recipient full names entered are invalid';
    }

    if (!values?.idNumber || !isValidID(stripWhitespace(values.idNumber))) {
      errors.idNumber = 'The recipient ID number entered is invalid';
    }

    if (
      values.idNumber &&
      isValidID(stripWhitespace(values.idNumber)) &&
      isUserUnderAgeIdNumber(values.idNumber)
    ) {
      errors.idNumber = 'The age limit is 18 years or older';
    }

    if (!isValidMobileNumber(stripWhitespace(values.cellPhone)) || !values?.cellPhone) {
      errors.cellPhone = 'The recipient cell number entered is invalid';
    }

    if (!isValidMobileNumber(stripWhitespace(values.workPhone)) || !values?.workPhone) {
      errors.workPhone = 'The recipient work number entered is invalid';
    }

    if (!hasNoSpecialCharacters(values.tradingAs) || !values?.tradingAs) {
      errors.tradingAs = 'The trading name entered is invalid';
    }

    return errors;
  };

  const isSolutionsValid = (solutions: Solution[]) => {
    return (
      solutions.length > 0 &&
      solutions.findIndex(
        (solution) => !(solution.productId && solution.acquiringChannel && solution.quantity > 0),
      ) === -1
    );
  };

  function validateStepOne(values: Delivery) {
    const errors: FormikErrors<Delivery> = {};

    const addressErrors = validateAddressField(values.address);
    if (addressErrors) {
      errors.address = addressErrors;
    }
    return errors;
  }

  function validateStepTwo(values: Delivery) {
    const errors: FormikErrors<Delivery> = {};

    const recipientErrors = validateRecipient(values.receiverDetails);
    if (Object.values(recipientErrors).length > 0) {
      errors.receiverDetails = recipientErrors;
    }
    return errors;
  }

  function validateStepThree(values: Delivery) {
    const errors: FormikErrors<ModalStepThreeErrors> = {};

    if (
      props.application.brokerLogin &&
      !(Object.keys(values.newDelivery ? values.newDelivery[0] : {}).length > 0)
    ) {
      errors.newDelivery = ['Please select new delivery address'];
    }

    if (!isSolutionsValid(values.solutions)) {
      errors.solutions = ['At least one solution needs to be added'];
    }

    return errors;
  }

  function hasErrors(errors: FormikErrors<Delivery | ModalStepThreeErrors>) {
    return (
      (errors.newDelivery && errors.newDelivery.length > 0) ||
      (errors.solutions && errors.solutions?.length > 0) ||
      Object.values(errors).length > 0
    );
  }

  function validate(values: Delivery, stepIndex: number) {
    let errors: FormikErrors<Delivery | ModalStepThreeErrors> = {};

    switch (stepIndex) {
      case 0:
        errors = validateStepOne(values);
        break;
      case 1:
        errors = validateStepTwo(values);
        break;
      case 2:
        errors = validateStepThree(values);
        break;
      default:
        return {};
    }

    setNextButtonEnabled(hasErrors(errors));

    return errors;
  }

  function isAnyFormErrors(values: Delivery, stepIndex: number) {
    const errors = validate(values, stepIndex);

    return hasErrors(errors);
  }

  return (
    <Modal visible={props.show} allowTabs={true} onClose={() => props.setShow(false)}>
      <Toast />
      <Formik<Delivery>
        initialValues={getInitialValues()}
        validate={(values) => validate(values, currentStepIndex)}
        onSubmit={(values) => {
          setBackButtonEnabled(true);
          if (currentStepIndex == 2 && props.onSave) {
            orderTealiumService?.onAddressDeliveryDeviceAllocationEnd();
            const updatedDeliveries = updateDeliverySolution(
              values.solutions[0],
              props.valuesToEdit?.id as string,
              values.newDelivery?.[0]?.id as string,
              props.deliveryDetails,
              props.application.opportunity?.deliveries,
            );

            if (props.application.brokerLogin && props.jumpToStep === 2) {
              props.onSave({} as Delivery, updatedDeliveries);
              return;
            }

            props.onSave(
              {
                ...values,
                id: uuid(),
                address: {
                  ...values.address,
                  country: SOUTH_AFRICA,
                },
              },
              updatedDeliveries,
            );
          } else {
            if (
              currentStepIndex === 1 &&
              props.application.brokerLogin &&
              props.saveAddress &&
              !props.valuesToEdit
            ) {
              props.saveAddress({
                ...values,
                id: uuid(),
                address: {
                  ...values.address,
                  country: SOUTH_AFRICA,
                },
              });

              const addressExists = findAddress(
                values.address,
                props.application.opportunity?.deliveries || [],
              );

              notify({
                customId: uuid(),
                children: !addressExists
                  ? 'Address added successfully'
                  : 'Address updated successfully',
                autoClose: 500,
              });
            }

            if (currentStepIndex === 1 && props.isBrokerEditAddress) {
              props.onSave &&
                props.onSave({
                  ...values,
                  address: {
                    ...values.address,
                    country: SOUTH_AFRICA,
                  },
                });
              return;
            }

            navigateToNextStep();
          }
        }}
        innerRef={(formik) => {
          if (formik) {
            setOnNextEventHandler(formik.submitForm);
            setNextButtonEnabled(!isAnyFormErrors(formik.values, currentStepIndex));
            setBackButtonEnabled(true);
          }
        }}
        enableReinitialize={true}
      >
        {({ values }) => {
          return (
            <SimpleWizard
              currentStepIndex={currentStepIndex}
              navigateToNextStep={navigateToNextStep}
              navigateToPreviousStep={navigateToPreviousStep}
              brokerLogin={props.application.brokerLogin}
              steps={getSteps(values)}
              automationIdPrefix={props.automationIdSuffix}
            />
          );
        }}
      </Formik>
    </Modal>
  );

  function getFinalStepNextButtonLabel(isEditing: boolean) {
    if (isEditing) {
      return 'Update';
    } else {
      return 'Add';
    }
  }
}
